import CheckIcon from '@mui/icons-material/Check';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';

export type MultiSelectOption<T extends string | number> = {
    value: T;
    label: string;
    /** Optional second line, e.g. a locale tag under a language name. */
    secondary?: string;
};

type Props<T extends string | number> = {
    labelId?: string;
    id?: string;
    label?: string;
    value: T[];
    options: MultiSelectOption<T>[];
    onChange: (value: T[]) => void;
    disabled?: boolean;
};

/**
 * A multi-select whose selected rows are marked with a right-aligned check.
 *
 * The array-valued sibling of BitFlagSelect: same look, but for fields that store a LIST (locales, app
 * types) rather than a bitmask. Matching it matters more than it sounds - a left-hand checkbox and a
 * right-hand tick in the same product read as two different controls, and a user learns them separately.
 */
export default function MultiSelectCheck<T extends string | number>({
    labelId, id, label, value, options, onChange, disabled,
}: Props<T>) {
    const selected = value ?? [];

    const handleChange = (e: SelectChangeEvent<T[]>) => {
        const next = e.target.value;
        onChange((typeof next === 'string' ? [next as T] : next) as T[]);
    };

    return (
        <Select
            labelId={labelId}
            id={id}
            label={label}
            multiple
            disabled={disabled}
            value={selected}
            onChange={handleChange}
            renderValue={(picked) =>
                (picked as T[])
                    .map(v => options.find(o => o.value === v)?.label ?? String(v))
                    .filter(Boolean)
                    .join(', ')
            }
        >
            {options.map(option => (
                <MenuItem key={String(option.value)} value={option.value} sx={{ justifyContent: 'space-between', gap: 2 }}>
                    <span>
                        {option.label}
                        {option.secondary && (
                            <span style={{ opacity: 0.6, marginLeft: 8, fontSize: '0.85em' }}>{option.secondary}</span>
                        )}
                    </span>
                    {selected.includes(option.value) && <CheckIcon fontSize="small" />}
                </MenuItem>
            ))}
        </Select>
    );
}
